import { useState, useCallback, useRef, useEffect } from 'react';
import { FILE_ACCEPT_STRING, MAX_FILE_SIZE, MAX_FILES_PER_UPLOAD } from '../config/fileUpload';

interface DropOverlayProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  maxSize?: number; // in bytes
  children: React.ReactNode;
}

export function DropOverlay({
  onFilesSelected,
  disabled = false,
  maxFiles = MAX_FILES_PER_UPLOAD,
  maxSize = MAX_FILE_SIZE,
  children,
}: DropOverlayProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle drag events on the container
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      dragCounter.current++;
      if (e.dataTransfer.types.includes('Files')) {
        setIsDragging(true);
      }
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files).slice(0, maxFiles);
      const validFiles = files.filter((f) => f.size <= maxSize);

      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    },
    [disabled, maxFiles, maxSize, onFilesSelected]
  );

  // Reset drag counter when component unmounts or drag ends externally
  useEffect(() => {
    const handleWindowDragEnd = () => {
      dragCounter.current = 0;
      setIsDragging(false);
    };

    window.addEventListener('dragend', handleWindowDragEnd);
    window.addEventListener('drop', handleWindowDragEnd);

    return () => {
      window.removeEventListener('dragend', handleWindowDragEnd);
      window.removeEventListener('drop', handleWindowDragEnd);
    };
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []).slice(0, maxFiles);
      const validFiles = files.filter((f) => f.size <= maxSize);

      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }

      // Reset input
      e.target.value = '';
    },
    [maxFiles, maxSize, onFilesSelected]
  );

  return (
    <div
      className="relative h-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
        accept={FILE_ACCEPT_STRING}
      />

      {/* Children (chat window content) */}
      {children}

      {/* Overlay shown only when dragging */}
      {isDragging && (
        <div className="absolute inset-0 bg-blue-500/20 border-2 border-dashed border-blue-500 rounded-lg z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg text-center">
            <div className="text-3xl mb-2">📎</div>
            <p className="text-sm font-medium text-gray-700">Drop files here</p>
            <p className="text-xs text-gray-500 mt-1">PDF, images, spreadsheets, CAD files</p>
          </div>
        </div>
      )}
    </div>
  );
}
